import React, { useState, useEffect, useMemo } from 'react';
import * as apiClient from '../services/apiClient';
import { 
  Search, 
  X, 
  CreditCard, 
  Building2, 
  Filter, 
  ExternalLink, 
  Users, 
  MessageSquare, 
  Gift, 
  Sparkles, 
  Plane, 
  ShoppingBag, 
  ShieldCheck, 
  ArrowUpDown,
  Plus,
  Globe,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';

export default function ExploreCardsModal({
  isOpen,
  onClose,
  onOpenCommunity,
  onOpenRewards,
  onLinkCard
}) {
  // Navigation / Country state
  const [selectedCountry, setSelectedCountry] = useState(null); // 'AE' or null (for country selection)
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Cards & Filter states
  const [cards, setCards] = useState([]);
  const [banks, setBanks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [sortBy, setSortBy] = useState('popularity_desc');
  const [loading, setLoading] = useState(false);

  // Load countries when modal opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedCountry(null);
      return;
    }

    let isMounted = true;
    async function fetchCountries() {
      setLoadingCountries(true);
      try {
        const res = await apiClient.request('/catalog/countries');
        if (isMounted && Array.isArray(res)) {
          setCountries(res);
        }
      } catch (err) {
        console.error('Error fetching countries:', err);
      } finally {
        if (isMounted) setLoadingCountries(false);
      }
    }
    fetchCountries();
    return () => { isMounted = false; };
  }, [isOpen]);

  // Load cards and banks when country or sortBy changes
  useEffect(() => {
    if (!isOpen || !selectedCountry) return;
    let isMounted = true;

    async function fetchData() {
      setLoading(true);
      try {
        const [banksRes, cardsRes] = await Promise.all([
          apiClient.request(`/catalog/banks?country=${selectedCountry}`),
          apiClient.request(`/catalog/cards?country=${selectedCountry}&limit=250&sort=${sortBy}`)
        ]);
        if (isMounted) {
          setBanks(Array.isArray(banksRes) ? banksRes : []);
          setCards(cardsRes?.cards || []);
        }
      } catch (err) {
        console.error('Error fetching cards catalog:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => { isMounted = false; };
  }, [isOpen, selectedCountry, sortBy]);

  const filteredCards = useMemo(() => {
    return cards.filter(card => {
      if (selectedBank && card.bank_name !== selectedBank) return false;
      if (selectedTier && card.card_tier !== selectedTier) return false;
      if (searchTerm && searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = (card.card_name || '').toLowerCase().includes(q);
        const matchBank = (card.bank_name || '').toLowerCase().includes(q);
        const matchPerks = (card.unique_perks_desc || '').toLowerCase().includes(q);
        return matchName || matchBank || matchPerks;
      }
      return true;
    });
  }, [cards, selectedBank, selectedTier, searchTerm]);

  const tiers = useMemo(() => {
    const set = new Set();
    cards.forEach(c => { if (c.card_tier) set.add(c.card_tier); });
    return Array.from(set);
  }, [cards]);

  if (!isOpen) return null;

  // Fallback countries list if API is unreachable
  const displayCountries = countries.length > 0 ? countries : [
    { code: 'AE', name: 'United Arab Emirates', native_name: 'الإمارات العربية المتحدة', flag: '🇦🇪', card_count: 245, bank_count: 37, is_available: true, tagline: '245+ Cards, Cashback, Cinema BOGO, Lounges & Perks' },
    { code: 'SA', name: 'Saudi Arabia', native_name: 'المملكة العربية السعودية', flag: '🇸🇦', is_available: false, tagline: 'Coming Soon' },
    { code: 'QA', name: 'Qatar', native_name: 'قطر', flag: '🇶🇦', is_available: false, tagline: 'Coming Soon' },
    { code: 'BH', name: 'Bahrain', native_name: 'البحرين', flag: '🇧🇭', is_available: false, tagline: 'Coming Soon' },
    { code: 'KW', name: 'Kuwait', native_name: 'الكويت', flag: '🇰🇼', is_available: false, tagline: 'Coming Soon' },
    { code: 'OM', name: 'Oman', native_name: 'عُمان', flag: '🇴🇲', is_available: false, tagline: 'Coming Soon' },
    { code: 'IN', name: 'India', native_name: 'भारत', flag: '🇮🇳', is_available: false, tagline: 'Coming Soon' },
    { code: 'GB', name: 'United Kingdom', native_name: 'United Kingdom', flag: '🇬🇧', is_available: false, tagline: 'Coming Soon' },
    { code: 'US', name: 'United States', native_name: 'United States', flag: '🇺🇸', is_available: false, tagline: 'Coming Soon' }
  ];

  return (
    <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div 
        className="glass-panel modal-spring-enter"
        style={{
          width: '95%',
          maxWidth: '850px',
          height: '90vh',
          maxHeight: '820px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '20px',
          overflow: 'hidden',
          background: 'var(--bg-main, #0f111a)',
          border: '1px solid var(--border-glass)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
        }}
      >
        {/* ======================================================== */}
        {/* STEP 1: COUNTRY SELECTION SCREEN                        */}
        {/* ======================================================== */}
        {!selectedCountry ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid var(--border-glass)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(0,245,212,0.25), rgba(84,160,255,0.25))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-cyan)'
                }}>
                  <Globe size={22} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-main)' }}>
                    Select Your Country
                  </h2>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Choose a region to explore verified credit cards, cashback terms, and perks
                  </span>
                </div>
              </div>
              <button 
                type="button" 
                onClick={onClose} 
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '6px', borderRadius: '8px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Country List / Grid */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700', color: 'var(--accent-cyan)' }}>
                  Available Directory
                </span>
              </div>

              {/* Active Available Country: UAE */}
              {displayCountries.filter(c => c.is_available).map(country => (
                <div
                  key={country.code}
                  onClick={() => setSelectedCountry(country.code)}
                  className="glass-panel"
                  style={{
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1.5px solid rgba(0,245,212,0.4)',
                    background: 'linear-gradient(135deg, rgba(0,245,212,0.06), rgba(165,94,234,0.06))',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 8px 24px rgba(0,245,212,0.1)',
                    transition: 'transform 0.15s, border-color 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-cyan)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,245,212,0.4)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ fontSize: '38px', lineHeight: 1 }}>{country.flag}</div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-main)' }}>
                          {country.name}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '800',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          background: 'rgba(46,213,115,0.2)',
                          color: 'var(--color-inflow)',
                          border: '1px solid rgba(46,213,115,0.4)'
                        }}>
                          ACTIVE DIRECTORY
                        </span>
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--accent-cyan)', fontWeight: '600', display: 'block', marginTop: '2px' }}>
                        {country.card_count || '245+'} Verified Cards • {country.bank_count || '37'} Banks
                      </span>
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                        {country.tagline || 'Cashback, Cinema BOGO, Airport Lounges & Lifestyle Perks'}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent-cyan)', fontWeight: '700', fontSize: '13px' }}>
                    Explore Cards <ChevronRight size={18} />
                  </div>
                </div>
              ))}

              {/* Upcoming Regions */}
              <div style={{ marginTop: '16px', marginBottom: '4px' }}>
                <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Upcoming Regions (Coming Soon)
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                gap: '12px'
              }}>
                {displayCountries.filter(c => !c.is_available).map(country => (
                  <div
                    key={country.code}
                    className="glass-panel"
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      background: 'rgba(255, 255, 255, 0.015)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      opacity: 0.65
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>{country.flag}</span>
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)', display: 'block' }}>
                          {country.name}
                        </span>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          {country.native_name || country.code}
                        </span>
                      </div>
                    </div>
                    <span style={{
                      fontSize: '9px',
                      fontWeight: '700',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'rgba(255, 255, 255, 0.06)',
                      color: 'var(--text-muted)'
                    }}>
                      Coming Soon
                    </span>
                  </div>
                ))}
              </div>

              {/* Notice */}
              <div style={{
                marginTop: '16px',
                padding: '14px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '11px',
                color: 'var(--text-muted)'
              }}>
                <Sparkles size={16} color="var(--accent-purple)" style={{ flexShrink: 0 }} />
                <span>
                  Currently, the comprehensive credit cards directory is seeded for the <strong>United Arab Emirates</strong>. Additional country directories will be released in subsequent catalog updates.
                </span>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 24px',
              borderTop: '1px solid var(--border-glass)',
              background: 'rgba(255, 255, 255, 0.02)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                style={{ padding: '8px 20px', borderRadius: '10px', fontSize: '12px' }}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          /* ======================================================== */
          /* STEP 2: CARDS DIRECTORY VIEW (For selected country)      */
          /* ======================================================== */
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid var(--border-glass)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.02)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(165,94,234,0.3), rgba(84,160,255,0.3))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-purple)'
                }}>
                  <CreditCard size={20} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: 'var(--text-main)' }}>
                      🇦🇪 UAE Credit Cards Directory
                    </h2>
                    <button
                      type="button"
                      onClick={() => setSelectedCountry(null)}
                      style={{
                        padding: '3px 8px',
                        fontSize: '11px',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid var(--border-glass)',
                        color: 'var(--accent-cyan)',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontWeight: '600'
                      }}
                      title="Switch country region"
                    >
                      <ArrowLeft size={12} /> Change Country
                    </button>
                  </div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Explore 245+ UAE cards, rewards matrices, and verified cardholder communities
                  </span>
                </div>
              </div>
              <button 
                type="button" 
                onClick={onClose} 
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '6px', borderRadius: '8px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Filter Controls Bar */}
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid var(--border-glass)',
              background: 'rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {/* Search Input */}
              <div style={{ position: 'relative', width: '100%' }}>
                <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: '12px', top: '11px' }} />
                <input
                  type="text"
                  placeholder="Search by card name, bank, cashback, lounge access, or perks..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '9px 12px 9px 36px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-glass)',
                    color: 'var(--text-main)',
                    fontSize: '13px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Dropdown Filters Row */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Bank Filter */}
                <div style={{ flex: '1 1 180px', minWidth: '150px' }}>
                  <select
                    value={selectedBank}
                    onChange={e => setSelectedBank(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-glass)',
                      color: 'var(--text-main)',
                      fontSize: '12px'
                    }}
                  >
                    <option value="">All Banks ({banks.length})</option>
                    {banks.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                {/* Tier Filter */}
                <div style={{ flex: '1 1 140px', minWidth: '130px' }}>
                  <select
                    value={selectedTier}
                    onChange={e => setSelectedTier(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-glass)',
                      color: 'var(--text-main)',
                      fontSize: '12px'
                    }}
                  >
                    <option value="">All Tiers</option>
                    {tiers.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Sort Filter */}
                <div style={{ flex: '1 1 160px', minWidth: '150px' }}>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-glass)',
                      color: 'var(--text-main)',
                      fontSize: '12px'
                    }}
                  >
                    <option value="popularity_desc">Most Popular with Users</option>
                    <option value="name_asc">Bank Name (A-Z)</option>
                    <option value="fee_asc">Lowest Annual Fee</option>
                    <option value="salary_asc">Lowest Min Salary</option>
                  </select>
                </div>

                {/* Reset Filters */}
                {(searchTerm || selectedBank || selectedTier) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedBank('');
                      setSelectedTier('');
                    }}
                    style={{
                      padding: '7px 12px',
                      borderRadius: '8px',
                      background: 'rgba(255, 71, 87, 0.15)',
                      border: '1px solid rgba(255, 71, 87, 0.3)',
                      color: 'var(--color-outflow)',
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            </div>

            {/* Catalog List Feed */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  <Sparkles size={24} color="var(--accent-purple)" className="animate-spin" style={{ margin: '0 auto 10px' }} />
                  <p style={{ margin: 0, fontSize: '13px' }}>Loading UAE credit cards directory...</p>
                </div>
              ) : filteredCards.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
                  <CreditCard size={36} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: 'var(--text-main)' }}>No Credit Cards Found</h3>
                  <p style={{ margin: 0, fontSize: '12px' }}>Try adjusting your search terms or clearing bank/tier filters.</p>
                </div>
              ) : (
                filteredCards.map(card => {
                  const annualFeeText = card.annual_fee === 0 ? 'Free for Life' : `AED ${Number(card.annual_fee).toLocaleString()}`;
                  const minSalaryText = card.min_salary > 0 ? `AED ${Number(card.min_salary).toLocaleString()}/mo` : 'No Min Stated';

                  return (
                    <div
                      key={card.card_id}
                      className="glass-panel"
                      style={{
                        padding: '16px',
                        borderRadius: '16px',
                        border: '1px solid var(--border-glass)',
                        background: 'rgba(255, 255, 255, 0.02)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        transition: 'border-color 0.2s, transform 0.15s'
                      }}
                    >
                      {/* Top Row: Identity + Tiers */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, rgba(165,94,234,0.25), rgba(84,160,255,0.25))',
                            border: '1px solid rgba(165,94,234,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--accent-purple)',
                            flexShrink: 0
                          }}>
                            <CreditCard size={20} />
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-main)' }}>
                                {card.card_name}
                              </span>
                              {card.card_tier && (
                                <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: 'rgba(165,94,234,0.15)', color: 'var(--accent-purple)' }}>
                                  {card.card_tier}
                                </span>
                              )}
                              {card.is_linked_by_user && (
                                <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: 'rgba(46,213,115,0.15)', color: 'var(--color-inflow)' }}>
                                  ✓ In My Wallet
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                              {card.bank_name}
                            </span>
                          </div>
                        </div>

                        {/* Stats Badges */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            background: 'rgba(255, 255, 255, 0.04)',
                            color: 'var(--text-muted)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Users size={12} color="var(--accent-cyan)" /> {card.cardholders_count || 0} cardholders
                          </span>
                          <span style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            background: 'rgba(255, 255, 255, 0.04)',
                            color: 'var(--text-muted)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <MessageSquare size={12} color="var(--accent-purple)" /> {card.posts_count || 0} posts
                          </span>
                        </div>
                      </div>

                      {/* Middle Row: Financial Quick Pills */}
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{
                          padding: '6px 10px',
                          borderRadius: '8px',
                          background: card.annual_fee === 0 ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                          border: card.annual_fee === 0 ? '1px solid rgba(46, 213, 115, 0.3)' : '1px solid rgba(255, 255, 255, 0.06)',
                          fontSize: '11px',
                          color: card.annual_fee === 0 ? 'var(--color-inflow)' : 'var(--text-main)',
                          fontWeight: '700'
                        }}>
                          Annual Fee: {annualFeeText}
                        </div>

                        <div style={{
                          padding: '6px 10px',
                          borderRadius: '8px',
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          fontSize: '11px',
                          color: 'var(--text-main)'
                        }}>
                          Min Salary: {minSalaryText}
                        </div>

                        {card.max_cashback_monthly && card.max_cashback_monthly !== 'No cap' && (
                          <div style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            background: 'rgba(255, 176, 58, 0.1)',
                            border: '1px solid rgba(255, 176, 58, 0.3)',
                            fontSize: '11px',
                            color: '#ffb03a',
                            fontWeight: '600'
                          }}>
                            Cap: AED {card.max_cashback_monthly}/mo
                          </div>
                        )}

                        {card.welcome_bonus && (
                          <div style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            background: 'rgba(165, 94, 234, 0.1)',
                            border: '1px solid rgba(165, 94, 234, 0.3)',
                            fontSize: '11px',
                            color: 'var(--accent-purple)',
                            fontWeight: '600'
                          }}>
                            Bonus: {card.welcome_bonus}
                          </div>
                        )}
                      </div>

                      {/* Benefits & Perks Snippet */}
                      {card.unique_perks_desc && (
                        <p style={{
                          margin: 0,
                          fontSize: '11px',
                          color: 'var(--text-muted)',
                          lineHeight: '1.4',
                          background: 'rgba(0,0,0,0.15)',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          borderLeft: '3px solid var(--accent-purple)'
                        }}>
                          {card.unique_perks_desc}
                        </p>
                      )}

                      {/* Bottom Row: Actions */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '10px', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              onOpenRewards(card);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: 'rgba(165,94,234,0.12)',
                              border: '1px solid rgba(165,94,234,0.3)',
                              color: 'var(--accent-purple)',
                              fontSize: '11px',
                              cursor: 'pointer',
                              fontWeight: '700',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <Gift size={13} /> View Rewards
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              onOpenCommunity(card);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: 'rgba(0,245,212,0.1)',
                              border: '1px solid rgba(0,245,212,0.25)',
                              color: 'var(--accent-cyan)',
                              fontSize: '11px',
                              cursor: 'pointer',
                              fontWeight: '700',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <MessageSquare size={13} /> Card Community
                          </button>
                        </div>

                        <div>
                          {!card.is_linked_by_user ? (
                            <button
                              type="button"
                              onClick={() => {
                                onClose();
                                onLinkCard(card);
                              }}
                              className="btn-primary"
                              style={{
                                padding: '6px 14px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Plus size={13} /> Link Card
                            </button>
                          ) : (
                            <span style={{ fontSize: '11px', color: 'var(--color-inflow)', fontWeight: '600' }}>
                              ✓ Linked
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid var(--border-glass)',
              background: 'rgba(255, 255, 255, 0.02)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '11px',
              color: 'var(--text-muted)'
            }}>
              <span>Showing {filteredCards.length} of {cards.length} official credit cards in UAE</span>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--accent-cyan)',
                  cursor: 'pointer',
                  fontWeight: '700'
                }}
              >
                Close Directory
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
